import {
  DropdownFieldType,
  FieldOption,
  FieldType,
  FormListsMap,
  MultipleChoiceFieldType,
  SingleChoiceFieldType,
} from './types';

export type OptionsField = DropdownFieldType | SingleChoiceFieldType | MultipleChoiceFieldType;

const OPTIONS_FIELD_KINDS: ReadonlySet<FieldType['kind']> = new Set([
  'input-dropdown',
  'input-single-choice',
  'input-multiple-choice',
]);

export const isOptionsField = (field: FieldType): field is OptionsField => {
  return OPTIONS_FIELD_KINDS.has(field.kind);
};

// Matches on value OR label: the form builder authors excludedOptions as
// values, but the Navigator (AI builder) authors them as labels. Mirrors the
// backend filter_out_excluded_options and the useListOptions filter.
export const filterExcludedOptions = (
  options: FieldOption[],
  excludedOptions: string[] | null = []
): FieldOption[] => {
  const excluded = excludedOptions ?? [];
  return options
    .filter((option) => !excluded.includes(option.value) && !excluded.includes(option.label))
    .map((option) => ({ ...option }));
};

/**
 * The options to render for an options field. List-backed fields hydrate from
 * the `lists` map shipped alongside the form (their inline `options` are empty);
 * everything else uses the inline options the builder defined. Falls back to
 * inline options when the referenced list isn't in the map (e.g. the builder
 * preview, which still inlines options and ships no `lists`).
 */
export const resolveFieldOptions = (field: OptionsField, lists: FormListsMap): FieldOption[] => {
  if (field.optionsFromListId && lists[field.optionsFromListId]) {
    return filterExcludedOptions(lists[field.optionsFromListId], field.excludedOptions);
  }
  return field.options;
};

/**
 * The `options` to put on a field when submitting it.
 *
 * For a list-backed field, returns just the option(s) the user picked, looked
 * up from `lists` by the field's value. We keep the picked option (not just the
 * value) because the submission view shows the answer by finding `value` in
 * `options` and reading its label — an empty `options` would render "No answer".
 * Returning only the picked option also keeps the payload small, so a huge list
 * can't blow past the request size limit.
 *
 * For a non-list (manually-authored) field, returns its inline options as-is.
 */
export const selectedListOptions = (field: OptionsField, lists: FormListsMap): FieldOption[] => {
  const listOptions = field.optionsFromListId ? lists[field.optionsFromListId] : undefined;
  if (!listOptions) {
    return field.options;
  }

  const value = field.value;
  if (Array.isArray(value)) {
    const selected = new Set(value);
    return filterExcludedOptions(
      listOptions.filter((option) => selected.has(option.value)),
      field.excludedOptions
    );
  }

  if (value == null || value === '') {
    return [];
  }

  const match = listOptions.find((option) => option.value === value);
  return match ? [{ ...match }] : [];
};
